// Customer profile RPCs — trusted wrappers.
//
// Supabase is authoritative for:
//   * The `customers` row bound to the current auth.uid()
//   * Whether the customer profile is "complete" (first_name, last_name,
//     email all present and valid)
//   * The outcome / error taxonomy when updating fields
//
// React never fabricates completeness, validates email against a
// backend uniqueness rule, or hardcodes any success/failure copy that
// belongs to the RPC.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:profile]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

// get_customer_profile() — returns the current customer's identity
// fields plus a completeness flag. Called after OTP verify and again
// from the ProfileGate on protected routes.
export async function rpcGetCustomerProfile() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Your account is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_customer_profile');
    if (error) {
      logSanitized('get_customer_profile:error', error);
      return { ok: false, data: null, error: 'We could not load your account details. Please try again.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_customer_profile:threw', err);
    return { ok: false, data: null, error: 'We could not load your account details. Please try again.' };
  }
}

// update_customer_profile(p_first_name, p_last_name, p_email) —
// persists identity fields. Backend validates format and uniqueness
// and returns a controlled `outcome` string.
export async function rpcUpdateCustomerProfile({ firstName, lastName, email }) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Your account is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('update_customer_profile', {
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
    });
    if (error) {
      logSanitized('update_customer_profile:error', error);
      return { ok: false, data: null, error: 'We could not save your details. Please try again.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('update_customer_profile:threw', err);
    return { ok: false, data: null, error: 'We could not save your details. Please try again.' };
  }
}

// ---------------------------------------------------------------
// Tolerant readers — the backend can wrap the profile inside a
// row or return it flat. We look for the field under a handful of
// canonical names and never fabricate a value.
// ---------------------------------------------------------------

function pickString(payload, keys) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

function pickBool(payload, keys) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'boolean') return v;
  }
  return null;
}

export function readProfilePhone(payload) {
  return pickString(payload, ['phone', 'phone_e164', 'phone_number']);
}
export function readProfileFirstName(payload) {
  return pickString(payload, ['first_name', 'firstName', 'given_name']);
}
export function readProfileLastName(payload) {
  return pickString(payload, ['last_name', 'lastName', 'family_name']);
}
export function readProfileEmail(payload) {
  return pickString(payload, ['email', 'email_address']);
}

// True only when the backend explicitly asserts completeness.
// Never inferred client-side — if the flag is missing, we treat the
// profile as "unknown" (caller decides how to fail safe).
export function readProfileIsComplete(payload) {
  const v = pickBool(payload, ['is_complete', 'complete', 'profile_complete']);
  if (v !== null) return v;
  // Some backends may return an outcome-style string.
  const outcome = pickString(payload, ['outcome', 'status']);
  if (outcome === 'complete' || outcome === 'already_complete') return true;
  if (outcome === 'incomplete') return false;
  return null;
}

export function readProfileOutcome(payload) {
  return pickString(payload, ['outcome', 'status']);
}

// Map controlled update_customer_profile outcomes to customer-safe
// inline error copy. Anything unrecognised is treated as a generic
// retryable error; the RPC is authoritative for the exact copy but
// we render a stable string so the UI never blanks out.
export function updateOutcomeToInlineError(outcome) {
  switch (outcome) {
    case 'email_taken':
    case 'email_in_use':
    case 'duplicate_email':
      return 'That email is already used on another account. Please use a different email.';
    case 'invalid_email':
    case 'email_invalid':
      return 'That email address does not look valid. Please double-check it.';
    case 'invalid_first_name':
    case 'first_name_invalid':
      return 'Please enter a valid first name.';
    case 'invalid_last_name':
    case 'last_name_invalid':
      return 'Please enter a valid last name.';
    case 'unauthenticated':
    case 'not_signed_in':
      return 'Your session has expired. Please sign in again.';
    default:
      return null;
  }
}

// Outcomes we treat as "profile is now good, continue on".
export function isUpdateSuccessOutcome(outcome) {
  return (
    outcome === 'updated' ||
    outcome === 'no_changes' ||
    outcome === 'already_complete' ||
    outcome === 'complete' ||
    outcome === 'ok'
  );
}

// Standard email format check. Backend still owns uniqueness.
export function isValidEmailFormat(email) {
  if (typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length < 3 || trimmed.length > 254) return false;
  // RFC 5322 "lite" — matches the vast majority of real-world emails
  // without pulling in a heavy library.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}
